import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type {
  BulkCreateBody,
  BulkCreateResult,
  BulkErrorEnvelope,
  BulkRowError,
  CreateLogsheetMakerBody,
  LogsheetDocument,
  LogsheetMakerDetail,
  LogsheetMakerListItem,
  LogsheetMakerListQuery,
  MakerWorkOrderListItem,
  UpdateLogsheetMakerBody,
} from '@/types/logsheets-maker';
import type { PaginatedResult } from '@/types/roles';

/**
 * Plan 12-03 — typed wrappers around the 9 `/api/logsheets/maker` endpoints
 * defined in Plan 12-02.
 *
 * Each wrapper:
 *  - Throws an `ApiError` (via `toApiError`) on non-2xx so consumers can
 *    `instanceof`-switch on `e.status`.
 *  - Returns the typed response object on success.
 *
 * Permission gating is UI-only convenience (T-12-22). The backend re-checks
 * every permission via `[RequirePermission(Permissions.LogsheetMaker.X)]`
 * (Plan 12-02).
 */

function buildListUrl(q: LogsheetMakerListQuery): string {
  const params = new URLSearchParams();
  if (q.q !== undefined && q.q !== '') params.set('q', q.q);
  if (q.status !== undefined) params.set('status', q.status);
  if (q.fromDate !== undefined && q.fromDate !== '') params.set('fromDate', q.fromDate);
  if (q.toDate !== undefined && q.toDate !== '') params.set('toDate', q.toDate);
  if (q.siteId !== undefined) params.set('siteId', String(q.siteId));
  if (q.machineId !== undefined) params.set('machineId', String(q.machineId));
  params.set('page', String(q.page ?? 1));
  params.set('pageSize', String(q.pageSize ?? 20));
  return `/api/logsheets/maker?${params.toString()}`;
}

/**
 * REVIEW FF-028: accept an optional `AbortSignal` so the bulk-wizard
 * duplicate-check effect can cancel the in-flight list call when the user
 * navigates away from step 4 before the response arrives. Mirrors the FF-012
 * abort pattern threaded through `uploadMonthlyDocumentsRequest`.
 */
export async function listLogsheetsMakerRequest(
  query: LogsheetMakerListQuery = {},
  options: { readonly signal?: AbortSignal } = {},
): Promise<PaginatedResult<LogsheetMakerListItem>> {
  try {
    const { data } = await api.get<PaginatedResult<LogsheetMakerListItem>>(
      buildListUrl(query),
      options.signal ? { signal: options.signal } : undefined,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getLogsheetMakerRequest(id: number): Promise<LogsheetMakerDetail> {
  try {
    const { data } = await api.get<LogsheetMakerDetail>(`/api/logsheets/maker/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function createLogsheetMakerRequest(
  body: CreateLogsheetMakerBody,
): Promise<LogsheetMakerDetail> {
  try {
    const { data } = await api.post<LogsheetMakerDetail>('/api/logsheets/maker', body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function updateLogsheetMakerRequest(
  id: number,
  body: UpdateLogsheetMakerBody,
): Promise<LogsheetMakerDetail> {
  try {
    const { data } = await api.put<LogsheetMakerDetail>(`/api/logsheets/maker/${String(id)}`, body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function submitLogsheetMakerRequest(id: number): Promise<LogsheetMakerDetail> {
  try {
    const { data } = await api.post<LogsheetMakerDetail>(
      `/api/logsheets/maker/${String(id)}/submit`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function deleteLogsheetMakerRequest(id: number): Promise<void> {
  try {
    await api.delete(`/api/logsheets/maker/${String(id)}`);
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * Multipart upload — wraps the file in a FormData and forwards through axios with
 * `Content-Type: multipart/form-data`. The optional `onProgress` callback receives
 * an integer 0..100 derived from `axios.onUploadProgress` (T-12-25 size-bypass guard
 * is enforced server-side; this wrapper does not pre-validate size — the caller
 * `DocumentUploadZone` performs the client-side 10 MiB check).
 */
export async function uploadLogsheetDocumentRequest(
  entryId: number,
  file: File,
  onProgress?: (percent: number) => void,
  signal?: AbortSignal,
): Promise<LogsheetDocument> {
  try {
    const form = new FormData();
    form.append('file', file);
    // The global axios instance defaults to `Content-Type: application/json`. For
    // multipart/form-data uploads we must clear that header so axios + the platform
    // fetch implementation set the correct `Content-Type: multipart/form-data;
    // boundary=...` value automatically from the FormData payload.
    //
    // We bypass the axios JSON default by:
    //  1) using `transformRequest` that removes the Content-Type header before send
    //  2) returning the FormData body as-is (axios's default transformer would
    //     attempt to JSON.stringify it).
    const r = await api.request<LogsheetDocument>({
      url: `/api/logsheets/maker/${String(entryId)}/documents`,
      method: 'post',
      data: form,
      // REVIEW-2 NF-4: optional AbortSignal so the Maker upload zone can
      // cancel an in-flight upload (mirrors FF-012 bulk-wizard pattern).
      // Only include the property when the caller provided a signal — the
      // axios type rejects `signal: undefined` under exactOptionalPropertyTypes.
      ...(signal !== undefined ? { signal } : {}),
      transformRequest: (d, headers) => {
        (headers as { delete?: (k: string) => unknown }).delete?.('Content-Type');
        delete (headers as Record<string, unknown>)['Content-Type'];
        delete (headers as Record<string, unknown>)['content-type'];
        return d as unknown;
      },
      onUploadProgress: (evt) => {
        if (onProgress !== undefined && typeof evt.total === 'number' && evt.total > 0) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      },
    });
    return r.data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function deleteLogsheetDocumentRequest(entryId: number, docId: number): Promise<void> {
  try {
    await api.delete(`/api/logsheets/maker/${String(entryId)}/documents/${String(docId)}`);
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * D-229 — Fetch all Active Work Orders (no siteId filter).
 * The maker wizard now selects WO first, then scopes Site to wo.sites.
 * `GET /api/logsheets/maker/work-orders`
 */
export async function getMakerActiveWorkOrdersRequest(): Promise<readonly MakerWorkOrderListItem[]> {
  try {
    const { data } = await api.get<readonly MakerWorkOrderListItem[]>(
      `/api/logsheets/maker/work-orders`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * Phase 17 Plan 17-02 D-162 — Fetch KPIs applicable to a specific (WO, machine) pair.
 * `GET /api/logsheets/maker/work-orders/:woId/kpis-for-machine?machineId=X`
 *
 * Used by the Phase 12 maker wizard's KPI rendering switch when a WO is selected.
 */
export async function getMakerKpisForWorkOrderMachineRequest(
  woId: number,
  machineId: number,
): Promise<import('@/types/logsheets-maker').MakerKpiForMachine[]> {
  try {
    const { data } = await api.get<import('@/types/logsheets-maker').MakerKpiForMachine[]>(
      `/api/logsheets/maker/work-orders/${String(woId)}/kpis-for-machine?machineId=${String(machineId)}`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/* ─── Phase 24 (v1.4) — Bulk Logsheet Create ─────────────────────────── */

/**
 * Thrown by `bulkCreateLogsheetMakerRequest` when the backend rejects the batch
 * with HTTP 400 + a `{ errors: BulkRowError[] }` body (SPEC.md BULK-13).
 *
 * Atomicity invariant: when this error fires, **zero rows were persisted**. The
 * wizard surfaces per-row issues inline (rowIndex >= 0) and bulk-level issues
 * at the top of the form (rowIndex === -1).
 */
export class BulkValidationError extends Error {
  readonly status = 400 as const;
  readonly errors: readonly BulkRowError[];
  constructor(errors: readonly BulkRowError[]) {
    super(
      `Bulk validation failed (${String(errors.length)} ${errors.length === 1 ? 'error' : 'errors'})`,
    );
    this.name = 'BulkValidationError';
    this.errors = errors;
  }
}

function isBulkErrorEnvelope(data: unknown): data is BulkErrorEnvelope {
  if (typeof data !== 'object' || data === null) return false;
  const errors: unknown = (data as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) return false;
  // Spot-check first row — accept loose shape, the caller renders raw fields.
  if (errors.length === 0) return true;
  const first: unknown = errors[0];
  return (
    typeof first === 'object' &&
    first !== null &&
    typeof (first as { errorCode?: unknown }).errorCode === 'string'
  );
}

/**
 * Phase 24 (v1.4) — `POST /api/logsheets/maker/bulk`.
 *
 * Creates 1..50 Draft logsheet entries in one transaction. On HTTP 400 with the
 * bulk-error envelope shape, throws `BulkValidationError` carrying the per-row
 * errors so the wizard can paint failing rows. Any other failure path (401, 403,
 * 500, network) throws a standard `ApiError`.
 */
export async function bulkCreateLogsheetMakerRequest(
  body: BulkCreateBody,
): Promise<BulkCreateResult> {
  try {
    const { data } = await api.post<BulkCreateResult>('/api/logsheets/maker/bulk', body);
    return data;
  } catch (e) {
    // axios attaches the parsed response body at `e.response.data` for non-2xx.
    const responseData =
      (e as { response?: { data?: unknown; status?: number } }).response?.data ?? null;
    const status = (e as { response?: { status?: number } }).response?.status ?? 0;
    if (status === 400 && isBulkErrorEnvelope(responseData)) {
      throw new BulkValidationError(responseData.errors);
    }
    throw toApiError(e);
  }
}
